import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
} from '@mui/material';
import { Close, WarningAmberOutlined } from '@mui/icons-material';

/**
* @function AlertDialogModal
* @description - child component of data ingestion and sub module of data management
- building UI of data ingestion dataset table
* @property {object} open - open modal of a particular project
* @property {object} rowData - row data
* @property {function} handleClose - close modal of a particular project
* @property {function} handleTask - to action a section of the document
* @property {object} ContentText - message that will show in DialogContentText
* @property {string} ButtonName - name of a button
* @property {bool} isCancel - checks if data is cancel or not
* @returns {React.ReactElement} AlertDialogModal UI
* @example <AlertDialogModal open={open} rowData={rowData} />
 */
const AlertDialogModal = ({
  open,
  rowData,
  handleClose,
  mode,
  handleConfirmMatchClose,
  handleTask,
  ContentText,
  ButtonName,
  isCancel,
  fontVariant = 'subtitle1',
  disableRestoreFocus,
}) => {
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: { borderRadius: 15 },
        }}
        disableRestoreFocus={disableRestoreFocus}
      >
        <Box>
          <DialogTitle id="alert-dialog-title">
            <Box display="flex" justifyContent="space-between">
              <Box
                sx={{
                  height: '30px',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <WarningAmberOutlined fontSize="large" color="primary" />

                <Typography variant="subtitle1" fontSize="20px">
                  Are You Sure?
                </Typography>
              </Box>
              <IconButton aria-label="close" onClick={handleClose} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider color="primary" />
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Typography variant={fontVariant} textAlign="center">
                {ContentText}
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button onClick={handleClose} variant="outlined" color="primary">
              {isCancel ? 'Cancel' : 'No'}{' '}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleTask(rowData);
                if (mode) {
                  handleConfirmMatchClose();
                } else {
                  handleClose();
                }
              }}
            >
              {ButtonName}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

export default React.memo(AlertDialogModal);

AlertDialogModal.propTypes = {
  open: PropTypes.bool.isRequired,
  rowData: PropTypes.oneOfType([PropTypes.object]),
  handleClose: PropTypes.func.isRequired,
  handleTask: PropTypes.func.isRequired,
  ContentText: PropTypes.string.isRequired,
  ButtonName: PropTypes.string.isRequired,
  isCancel: PropTypes.bool,
  mode: PropTypes.string,
  handleConfirmMatchClose: PropTypes.func,
  fontVariant: PropTypes.string,
  disableRestoreFocus: PropTypes.bool,
};

AlertDialogModal.defaultProps = {
  mode: null,
  handleConfirmMatchClose: () => {},
  isCancel: false,
  rowData: {},
  fontVariant: 'subtitle1',
  disableRestoreFocus: false,
};
